import React, {memo, useMemo, FC} from "react";
import {CardMarkupViewerProps, CardMarkupViewerState} from "./cardMarkupViewer.interface";
import {BlockMarkupViewer} from "../blockMarkupViewer/blockMarkupViewer";
import {VerticalSpace} from "../../general-components/verticalSpace/verticalSpace";
import {markupFormatter, subcardKey} from "../../common";
import styled from "styled-components";
import { ThemeProps } from "../../styles/theme";
import { SubcardDisplay } from "../../cards/multicard/preview/subcardDisplay";
import { ReferenceViewer } from "../referenceViewer/referenceViewer";

const Separator = styled.div<ThemeProps>`
    width: 100%;
    height: 3px;
    border-top: 3px dashed ${(props: ThemeProps) => props.theme.primaryBorder};
`;

// Создаем специальный компонент для блока examples с выравниванием по левому краю
const ExamplesContainer = styled.div`
    text-align: left;
    
    /* Переопределяем стили для текстовых элементов внутри блока examples */
    span {
        text-align: left;
    }
`;

const SubcardsContainer = styled.div<ThemeProps>`
    margin-top: 20px;
    width: 100%;
    box-sizing: border-box;
`;

const SubcardsTitle = styled.div<ThemeProps>`
    font-size: 13px;
    font-weight: 600;
    color: ${props => props.theme.secondaryText};
    margin-bottom: 16px;
    text-align: center;
    padding-bottom: 8px;
    border-bottom: 1px solid ${props => props.theme.primaryBorder};
`;

const CardMarkupViewerComponent: FC<CardMarkupViewerProps> = memo((props) => {
    const {cardId, markup, state} = props;
    const cardMarkup = useMemo(() => markupFormatter.parse(markup), [markup]);

    const showHint = (state === CardMarkupViewerState.QuestionAndHint
        || state === CardMarkupViewerState.Full)
        && cardMarkup.hint.length > 0;
    
    const reference = props.referenceOverride ?? cardMarkup.reference;
    const showSeparators = props.showSeparators === true;

    const hasSubcards = Boolean(cardMarkup.hasSubCards) &&
                       Array.isArray(cardMarkup.subCards) && 
                       cardMarkup.subCards.length > 0;
    const showSubcards = hasSubcards && (state === CardMarkupViewerState.Full);

    return (
        <div>
            <BlockMarkupViewer
                cardId={cardId}
                blockMarkup={(cardMarkup.question)}
            />
            {showHint && (
                <>
                    <VerticalSpace height={10}/>
                    {props.showSeparators !== undefined && props.showSeparators === true && (
                        <Separator/>
                    )}
                    <VerticalSpace height={10}/>
                    <BlockMarkupViewer
                        cardId={cardId}
                        blockMarkup={(cardMarkup.hint)}
                    />
                </>
            )}
            {(state === CardMarkupViewerState.Full || state === CardMarkupViewerState.QuestionAndAnswer) && (
                <>
                    <VerticalSpace height={10}/>
                    {props.showSeparators !== undefined && props.showSeparators === true && (
                        <Separator/>
                    )}
                    <VerticalSpace height={10}/>
                    <BlockMarkupViewer
                        cardId={cardId}
                        blockMarkup={(cardMarkup.answer)}
                    />
                    {reference.length > 0 && (
                        <>
                            <VerticalSpace height={10}/>
                            {showSeparators && (
                                <Separator/>
                            )}
                            <VerticalSpace height={10}/>
                            <ReferenceViewer sections={reference} defaultExpanded={props.expandReference} cardId={cardId}/>
                        </>
                    )}
                    {cardMarkup.examples.length > 0 && (
                        <>
                            <VerticalSpace height={10}/>
                            {props.showSeparators !== undefined && props.showSeparators === true && (
                                <Separator/>
                            )}
                            <VerticalSpace height={10}/>
                            <ExamplesContainer>
                                <BlockMarkupViewer 
                                    cardId={cardId}
                                    blockMarkup={(cardMarkup.examples)}
                                />
                            </ExamplesContainer>
                        </>
                    )}
                    {cardMarkup.footer.length > 0 && (
                        <>
                            <VerticalSpace height={20}/>
                            <BlockMarkupViewer blockMarkup={(cardMarkup.footer)}/>
                        </>
                    )}
                </>
            )}
            {showSubcards && (
                <>
                    <VerticalSpace height={15}/>
                    {props.showSeparators !== undefined && props.showSeparators === true && (
                        <Separator/>
                    )}
                    <VerticalSpace height={10}/>
                    <SubcardsContainer>
                        <SubcardsTitle>
                            Subcards ({cardMarkup.subCards!.length})
                        </SubcardsTitle>
                        {cardMarkup.subCards!.map((subcard, index) => (
                            <SubcardDisplay
                                key={index}
                                subcard={subcard}
                                index={index}
                                isSelected={false}
                                isSelectable={false}
                                showCheckbox={false}
                                showIndex={false}
                                showAnswer={true}
                                cardId={cardId}
                                stat={props.subcardStats?.[subcardKey(subcard.question, subcard.answer)]}
                            />
                        ))}
                    </SubcardsContainer>
                </>
            )}
        </div>
    );
});

CardMarkupViewerComponent.displayName = 'CardMarkupViewerComponent';

export { CardMarkupViewerComponent as CardMarkupViewer };
